import { AlertTriangle, CheckCircle2 } from "lucide-react";

import { OutcomeBadge, ProbationBadge, SectionCard, fmtDate } from "../shared.js";
import { PROBATION_OUTCOME_DONE_LABELS, type ProbationInfo, type ReviewDetail } from "../types.js";
import ProbationFormBlock from "./probation/ProbationFormBlock.js";
import ProbationDecisionBlock from "./probation/ProbationDecisionBlock.js";

type AnchorProbation = Extract<ProbationInfo, { isThisReview: true }>;

// Probation is a status on the employee's annual review. This card sits on the
// review that carries it (the hire-year one): the probation dates, the new
// joiner's First 30 Days review (signed by the employee and the manager) and,
// for HR only, the decision. Everyone else sees the status.
export default function ProbationSection({
  review,
  probation: p,
  onChanged,
}: {
  review: ReviewDetail;
  probation: AnchorProbation;
  onChanged: () => void;
}) {
  const caps = review.capabilities;
  const extended = p.extensionCount > 0;

  const status = p.open
    ? extended
      ? `On probation · extended ${p.extensionCount}×`
      : "On probation"
    : p.outcome
      ? `${PROBATION_OUTCOME_DONE_LABELS[p.outcome]} · ${fmtDate(p.outcomeAt)}`
      : "Closed";

  return (
    <SectionCard
      collapsible
      defaultOpen={p.open}
      title="Probation"
      description="The first months after joining. The employee fills and signs the First 30 Days review, the manager signs, HR decides."
      actions={p.open ? <ProbationBadge extended={extended} /> : <OutcomeBadge outcome={p.outcome} />}
    >
      <div className="mb-5 grid gap-3 rounded-md bg-surface-50 px-4 py-3 text-[13px] sm:grid-cols-3">
        <Stat label="Probation started" value={fmtDate(p.periodStart)} />
        <Stat
          label="Probation ends"
          value={`${fmtDate(p.periodEnd)}${extended && p.previousEndDate ? ` (was ${fmtDate(p.previousEndDate)})` : ""}`}
        />
        <Stat label="Status" value={status} />
      </div>

      {!p.open && p.outcome && (
        <p className="mb-5 flex items-start gap-2 rounded-md bg-[var(--color-success-bg)] px-3 py-2 text-[13px] text-[var(--color-success-fg)]">
          <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
          Probation completed — {PROBATION_OUTCOME_DONE_LABELS[p.outcome].toLowerCase()} on {fmtDate(p.outcomeAt)}.
          Everything below is kept as the record.
        </p>
      )}

      {p.overdue && (
        <p className="mb-5 flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[12px] text-[var(--color-warning-fg)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          The probation end date has passed without a decision. The employee stays on probation until HR records one.
        </p>
      )}

      <div className="flex flex-col gap-6">
        <ProbationFormBlock
          reviewId={review.id}
          employeeName={review.header.fullName}
          probation={p}
          caps={caps}
          onChanged={onChanged}
        />
        {(caps.isHR || caps.isSuperuser) && (
          <ProbationDecisionBlock reviewId={review.id} probation={p} caps={caps} onChanged={onChanged} />
        )}
      </div>
    </SectionCard>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium tracking-wide text-surface-400 uppercase">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
